import { RefTypeEnum } from "@/interfaces/uniabc/RefTypeEnum";
import { readEntriesArray, readEntryByLabel } from "@/store/uniabc/getters";
import Entry from "@/interfaces/uniabc/Entry";
import { store } from "@/store";
import Footnote from "@/interfaces/uniabc/Footnote";
import { Language } from "@/interfaces/uniabc/Language";
import Reference from "@/interfaces/uniabc/Reference";

export default class Renderer {
  static readonly refRegEx = /\[([A-Za-z0-9]+)(\|[\w \u00C0-\u017F.,„“”/\\&'*()@#-]+)?\]/g;

  private static renderReferences(text: string, entry: Entry, lang: Language) {
    const refs = [...text.matchAll(this.refRegEx)];

    const allRefs = entry?.refs.concat(this.entriesAsRefs());

    if (allRefs) {
      refs.forEach(ref => {
        const reference =
          allRefs[allRefs.findIndex((r: Reference) => r.key === ref[1])];

        const overwrite = (ref[2] ?? "").substr(1);

        switch (reference?.reftype) {
          case RefTypeEnum.LABEL: {
            text = text.replace(
              ref[0],
              `<a class='label' title="label">${overwrite ||
                reference?.text}</a>`
            );
            break;
          }
          case RefTypeEnum.INTERNAL: {
            const referencedEntry = readEntryByLabel(store)(
              reference?.reference
            );
            const refEntrTerm =
              lang === Language.DE
                ? referencedEntry?.term_de
                : referencedEntry?.term_en;
            text = text.replace(
              ref[0],
              `<a class='internal' title="internal">${overwrite ||
                reference?.text ||
                refEntrTerm ||
                referencedEntry?.term_de}</a>`
            ); // href="/entry/${reference?.reference}" target="_self" onclick="event.preventDefault();app._router.push('/the-link/');"
            break;
          }
          case RefTypeEnum.DZHREF: {
            text = text.replace(
              ref[0],
              `<a class='dzhref' title="dzhref">${reference?.text} {${reference?.quickaccess}}</a>`
            );
            break;
          }
          case RefTypeEnum.URL: {
            text = text.replace(
              ref[0],
              `<a class='url' title="url">${reference?.reference}</a>`
            );
            break;
          }
          case RefTypeEnum.IMPORTANTURL: {
            text = text.replace(
              ref[0],
              `<a class='importanturl' style="font-weight: bold" title="importanturl">${reference?.reference}</a>`
            );
            break;
          }
        }
      });
    }

    return text;
  }

  private static renderStyle(text: string) {
    const itRegEx = /_([\w:/.?\-\s!,[\]~{}\\'|=@*`();ÄäÖöÜüß<>#"]+?)_/g;
    const bfRegEx = /\*\*([\w:/.?\-\s!,[\]~{}\\'|=@*`();ÄäÖöÜüß<>#"]+?)\*\*/g;
    const ttRegEx = /`([\w:/.?\-\s!,[\]~{}\\'|=@*`();ÄäÖöÜüß<>#"]+?)`/g;

    const itParts = [...text.matchAll(itRegEx)];

    itParts.forEach(it => {
      text = text.replace(it[0], `<span class='font-italic'>${it[1]}</span>`);
    });

    const bfParts = [...text.matchAll(bfRegEx)];

    bfParts.forEach(bf => {
      text = text.replace(
        bf[0],
        `<span class='font-weight-bold'>${bf[1]}</span>`
      );
    });

    const ttParts = [...text.matchAll(ttRegEx)];

    ttParts.forEach(tt => {
      text = text.replace(
        tt[0],
        `<span style="font-family: monospace">${tt[1]}</span>`
      );
    });

    return text;
  }

  public static renderedText(entry: Entry, lang: Language): string {
    let text = lang === Language.DE ? entry.text_de : entry.text_en;
    let footnotes = 0;

    const refsSubEntries = [...text.matchAll(this.refRegEx)];
    if (entry?.refs) {
      refsSubEntries.forEach(ref => {
        const reference =
          entry?.refs[
            entry?.refs.findIndex((r: Reference) => r.key === ref[1])
          ];

        switch (reference?.reftype) {
          case RefTypeEnum.SUBENTRY:
            text = text.replace(
              ref[0],
              this.renderedSubEntry(reference?.reference, reference?.text)
            );
            break;
        }
      });
    }

    const refsList = [...text.matchAll(this.refRegEx)];
    if (entry?.refs) {
      refsList.forEach(ref => {
        const reference =
          entry?.refs[
            entry?.refs.findIndex((r: Reference) => r.key === ref[1])
          ];

        switch (reference?.reftype) {
          case RefTypeEnum.LIST:
            text = text.replace(ref[0], this.renderedList(reference?.text));
            break;
        }
      });
    }

    const refsFootnotes = [...text.matchAll(this.refRegEx)];
    if (entry?.refs) {
      refsFootnotes.forEach(ref => {
        const reference =
          entry?.refs[
            entry?.refs.findIndex((r: Reference) => r.key === ref[1])
          ];

        switch (reference?.reftype) {
          case RefTypeEnum.FOOTNOTE:
            text = text.replace(ref[0], `<sup>${++footnotes}</sup>`);
            break;
        }
      });
    }

    text = `<p>${text}</p>`;
    text = text.replaceAll("\n\n", `</p><p>`);

    text = this.renderReferences(text, entry, lang);
    text = this.renderStyle(text);

    return text;
  }

  private static renderedList(text: string) {
    const listItems = text
      .split(`\n`)
      .filter(li => li !== "")
      .map(li => `<li>${li}`);
    return `<ul>${listItems}</ul>`;
  }

  private static renderedSubEntry(reference: string, text: string) {
    return `<div class="row"><div class="col-3 font-weight-bold text-right">${reference}</div><div class="col-9">${text}</div></div>`;
  }

  public static renderedFootnotes(entry: Entry, lang: Language): Footnote[] {
    const text = lang === Language.DE ? entry.text_de : entry.text_en;
    const footnotes: Footnote[] = [];

    const refs = [...text.matchAll(this.refRegEx)];
    if (entry?.refs) {
      refs.forEach(ref => {
        const reference =
          entry?.refs[
            entry?.refs.findIndex((r: Reference) => r.key === ref[1])
          ];

        if (reference?.reftype === RefTypeEnum.FOOTNOTE) {
          const identifier = footnotes.length + 1;
          footnotes.push({
            identifier: identifier,
            text: reference?.text
          });
        }
      });
    }

    footnotes.forEach(
      (f: Footnote) => (f.text = this.renderReferences(f.text, entry, lang))
    );
    footnotes.forEach((f: Footnote) => (f.text = this.renderStyle(f.text)));
    return footnotes;
  }

  private static toRefsMap(refs: Reference[]) {
    return refs.reduce(
      (obj: { [key: string]: Reference }, e: Reference) => (
        (obj[e.key] = e), obj
      ),
      {}
    );
  }

  private static entriesAsRefs(): Reference[] {
    return readEntriesArray(store)().map(e => ({
      key: e.label,
      reference: e.label,
      reftype: RefTypeEnum.INTERNAL,
      text: "",
      quickaccess: ""
    }));
  }
}
